import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ReactComponent as DeleteCustom } from "../../../src/shared/assets/deleteIcon.svg";
import { ADJUSTMENT_TYPES, GST_VALUES } from "../../shared/constants/constants";
import { isValidStringInput } from "../../shared/utils/validator";

interface AdjustmentDialogProps {
  open: boolean;
  prevAdjustments: any[];
  onConfirm: any;
  setOpen: any;
}

const AdjustmentDialog = ({
  prevAdjustments,
  open,
  onConfirm,
  setOpen,
}: AdjustmentDialogProps) => {
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<any>({});

  useEffect(() => {
    setAdjustments([...prevAdjustments]);
  }, [prevAdjustments]);

  const resetValidationError = (errorName: string) => {
    const validationObject = validationErrors;
    //reseting errors upon change
    if (errorName && errorName in validationErrors) {
      delete validationObject[errorName];
      setValidationErrors(validationObject);
    }
  };

  const validateDiscounts = () => {
    const errorObj: any = {}
    adjustments.forEach((adjustment, index) => {
      if (!adjustment?.description || adjustment?.description === "" || !isValidStringInput(adjustment?.description)) {
            errorObj[`description_${index}`] = "Invalid Description"
        }
      if (isNaN(adjustment?.adjustmentAmount) || adjustment?.adjustmentAmount <= 0) {
            errorObj[`adjustmentAmount_${index}`] = "Invalid Amount"
        }
      if (adjustment?.taxPercentage === undefined ||
        adjustment?.taxPercentage === null ||
        ![0, 5, 12, 18, 28].includes(adjustment?.taxPercentage)) {
            errorObj[`taxPercentage_${index}`] = "Invalid GST"
        }
    })
    if (Object.keys(errorObj)?.length > 0) {
        setValidationErrors({
            ...validationErrors,
            ...errorObj
        })
        return;
    }
    onConfirm(adjustments);
  }

  const handleInputChange = (
    index: number,
    name: string,
    value: any,
    errorName: string
  ) => {
    const itemsToUpdate = [...adjustments];

    resetValidationError(errorName);
    if (name === "gst") {
      itemsToUpdate[index].taxPercentage = value;
      itemsToUpdate[index].adjustmentTax =
        (Number(itemsToUpdate[index].taxPercentage) / 100) *
        Number(itemsToUpdate[index].adjustmentAmount);
      itemsToUpdate[index].adjustmentTotal =
        Number(itemsToUpdate[index].adjustmentAmount) +
        Number(itemsToUpdate[index].adjustmentTax);
    } else if (name === "amount") {
      let decimal = value.split(".");
      if (decimal[1]) {
        const maxDecimal = 2;
        if (decimal[1]?.length > maxDecimal) {
          decimal[1] = parseFloat(decimal[1].toString().slice(0, maxDecimal));
          const tempValue = decimal[0] + "." + decimal[1];
          value = parseFloat(tempValue);
        }
      }
      if (decimal[0] >= 1000000000) {
        decimal[0] = parseFloat(decimal[0].toString().slice(0, 9));
        const tempValue = decimal.join(".");
        value = parseFloat(tempValue);
      }
      itemsToUpdate[index].adjustmentAmount = value;
      itemsToUpdate[index].adjustmentTax =
        Number(itemsToUpdate[index].adjustmentAmount) *
        (Number(itemsToUpdate[index].taxPercentage) / 100);
      itemsToUpdate[index].adjustmentTotal =
        Number(itemsToUpdate[index].adjustmentAmount) +
        Number(itemsToUpdate[index].adjustmentTax);
    } else if (name === "desc") {
      itemsToUpdate[index].description = value;
    } else if (name === "adjustmentType") {
      itemsToUpdate[index].adjustmentType = value;
    }
    // console.log(itemsToUpdate);
    setAdjustments([...itemsToUpdate]);
  };

  const handleDeleteRequestRow = (index: number) => {
    if (adjustments.length === 0) {
      return;
    }
    setValidationErrors({});
    setAdjustments((prevRows) => {
      const currentRows = [...prevRows];
      currentRows.splice(index, 1);
      return currentRows;
    });
  }

  return (
    <Dialog open={open} fullWidth maxWidth={"md"}>
      <DialogTitle>
        <Typography fontSize={20} fontWeight={600} color={"#000000"}> Add Discounts </Typography>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{ width: "100%" }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ width: "100%" }}>
              <TableRow sx={{ width: "100%" }}>
                <TableCell sx={{ width: "22%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >Type</Typography>
                </TableCell>
                <TableCell sx={{ width: "22%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >Description</Typography>
                </TableCell>
                <TableCell sx={{ width: "22%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >Amount</Typography>
                </TableCell>
                <TableCell sx={{ width: "22%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >GST ( % )</Typography>
                </TableCell>
                <TableCell sx={{ width: "22%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >Total Amount</Typography>
                </TableCell>
                <TableCell sx={{ width: "12%", backgroundColor: "#F7F7F7" }}>
                  <Typography color={"#000000"} fontWeight={600} fontSize={14} >Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments?.map((adjustment: any, index: number) => {
                // console.log(adjustment);
                return (
                  <TableRow>
                    <TableCell>
                      <Select
                        fullWidth
                        size="small"
                        value={adjustment?.adjustmentType}
                        onChange={(event: any) => {
                          handleInputChange(
                            index,
                            "adjustmentType",
                            event.target.value,
                            `adjustmentType_${index}`
                          );
                        }}
                        sx={{
                          backgroundColor: "#f6f6f6",
                          "& input::placeholder": {
                            fontSize: "0.8rem",
                          },
                        }}
                        error={validationErrors?.[`adjustmentType_${index}`]}
                      >
                        {[
                        {
                          type: ADJUSTMENT_TYPES.DISCOUNT_ADJUSTMENT.id,
                          label: ADJUSTMENT_TYPES.DISCOUNT_ADJUSTMENT.label
                        },
                        {
                          type: ADJUSTMENT_TYPES.ROUND_OFF_ADJUSTMENT.id,
                          label: ADJUSTMENT_TYPES.ROUND_OFF_ADJUSTMENT.label
                        }
                      ]?.map(
                          (item: any) => (
                            <MenuItem value={item.type} sx={{ textAlign: "center" }}>
                              {item.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder={"Enter Description"}
                        size="small"
                        value={adjustment?.description}
                        onChange={(event: any) => {
                          handleInputChange(
                            index,
                            "desc",
                            event.target.value,
                            `description_${index}`
                          );
                        }}
                        sx={{
                          backgroundColor: "#f6f6f6",
                          "& input::placeholder": {
                            fontSize: "0.8rem",
                          },
                        }}
                        error={validationErrors?.[`description_${index}`]}
                      />
                      <FormHelperText
                        error={validationErrors?.[`description_${index}`]}
                      >
                        {validationErrors?.[`description_${index}`]}
                      </FormHelperText>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder={"Enter Amount"}
                        size="small"
                        type="number"
                        value={adjustment?.adjustmentAmount}
                        onChange={(event: any) => {
                          handleInputChange(
                            index,
                            "amount",
                            event.target.value,
                            `adjustmentAmount_${index}`
                          );
                        }}
                        sx={{
                          backgroundColor: "#f6f6f6",
                          "& input::placeholder": {
                            fontSize: "0.8rem",
                          },
                        }}
                        error={validationErrors?.[`adjustmentAmount_${index}`]}
                      />
                      <FormHelperText
                        error={validationErrors?.[`adjustmentAmount_${index}`]}
                      >
                        {validationErrors?.[`adjustmentAmount_${index}`]}
                      </FormHelperText>
                    </TableCell>
                    <TableCell>
                      <Select
                        fullWidth
                        size="small"
                        value={(typeof adjustment?.taxPercentage === "string" && adjustment?.taxPercentage?.includes("%")) ? Number(adjustment?.taxPercentage?.replace("%", "")) : adjustment?.taxPercentage}
                        onChange={(event: any) => {
                          handleInputChange(
                            index,
                            "gst",
                            event.target.value,
                            `taxPercentage_${index}`
                          );
                        }}
                        sx={{
                          backgroundColor: "#f6f6f6",
                          "& input::placeholder": {
                            fontSize: "0.8rem",
                          },
                        }}
                        error={validationErrors?.[`taxPercentage_${index}`]}
                        displayEmpty
                        renderValue={(selected: any) => {
                          if (
                            selected < 0 ||
                            selected === undefined ||
                            selected === null
                          ) {
                            return (
                              <p className="selectPlaceholder">
                                {"Select GST"}
                              </p>
                            );
                          }
                          return selected;
                        }}
                      >
                        {GST_VALUES?.map(
                          (item: any) => (
                            <MenuItem value={item} sx={{ textAlign: "center" }}>
                              {item}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      <FormHelperText
                        error={validationErrors?.[`taxPercentage_${index}`]}
                      >
                        {validationErrors?.[`taxPercentage_${index}`]}
                      </FormHelperText>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        {`₹${!Number.isNaN(Number(adjustment.adjustmentTotal))
                            ? Number(adjustment?.adjustmentTotal)?.toFixed(2)
                            : 0
                        }`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                        <IconButton
                        sx={{ color: "#ED1B24" }}
                            onClick={() => handleDeleteRequestRow(index)}
                        >
                            <DeleteCustom />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Stack direction={"row"} spacing={1}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#22438C",
              color: "#FFFFFF",
              borderRadius: 0,
              textTransform: "none",
            }}
            onClick={() =>
              setAdjustments([
                ...adjustments,
                {
                  description: "",
                  adjustmentAmount: 0,
                  taxPercentage: 0,
                  adjustmentTax: 0,
                  adjustmentTotal: 0,
                  adjustmentType: "DISCOUNT_ADJUSTMENT",
                },
              ])
            }
          >
            <Typography>Add Adjustment</Typography>
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#22438C",
              color: "#FFFFFF",
              borderRadius: 0,
              textTransform: "none",
            }}
            onClick={() => {
              validateDiscounts()
            }}
          >
            <Typography>Confirm</Typography>
          </Button>
          <Button
            variant="text"
            sx={{
              backgroundColor: "#F0F0F0",
              color: "#22438C",
              borderRadius: 0,
              textTransform: "none",
            }}
            onClick={() => setOpen(false)}
          >
            <Typography>Close</Typography>
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustmentDialog;
